import "./Slider.scss"

import { CSSProperties, FC, PropsWithChildren, ReactNode, useRef } from "react"
import { useRanger, Ranger } from '@tanstack/react-ranger'
import cn from "classnames"

import { Flex, FlexItem } from "components/Flex"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"

interface ISliderProps {
    min: number
    max: number
    step: number
    defaultValue: number | null
    defaultValueLabel?: ReactNode
    value: number | null
    valueLabel?: ReactNode
    scaleItems: {
        label: ReactNode
        left?: CSSProperties["left"]
        right?: CSSProperties["right"]
    }[]
    disabled?: boolean
    validationMessage?: string
    onChange(value: number): void
}

const Slider: FC<PropsWithChildren<ISliderProps>> = ({
    min,
    max,
    step,
    defaultValue,
    defaultValueLabel,
    value,
    valueLabel,
    scaleItems,
    disabled,
    validationMessage,
    onChange
}) => {
    const rangerRef = useRef<HTMLDivElement>(null)

    const defaultInstance = useRanger<HTMLDivElement>({
        getRangerElement: () => rangerRef.current,
        values: defaultValue !== null ? [defaultValue] : [],
        min,
        max,
        stepSize: step,
    })

    const mainInstance = useRanger<HTMLDivElement>({
        getRangerElement: () => rangerRef.current,
        values: value !== null ? [value] : [],
        min,
        max,
        stepSize: step,
        onChange: (instance: Ranger<HTMLDivElement>) => onChange(instance.sortedValues[0]),
    })

    function handleContainerMouseDown(event: React.MouseEvent<HTMLDivElement>) {
        if (disabled) return
        const { left, width } = rangerRef.current!.getBoundingClientRect()
        // set closest value to the click position, taking into consideration the step size, min and max values
        const newValue = Math.min(
            Math.max(
                Math.round(((event.clientX - left) / width) * (max - min) / step) * step + min,
                min
            ),
            max
        )
        onChange(newValue)
    }

    const defaultInstanceSteps = defaultInstance.getSteps()
    const mainInstanceSteps = mainInstance.getSteps()

    return (
        <div className={cn("slider", { "slider--disabled": disabled })}
            onMouseDown={handleContainerMouseDown}>
            {!!scaleItems.length &&
                <div className="slider__scale mb-1-5">
                    {scaleItems.map((item, index) => (
                        <div
                            key={index}
                            className="slider__scale-item"
                            style={{ left: item.left, right: item.right }}>
                            {item.label}
                        </div>
                    ))}
                </div>
            }

            <div className="slider__track" ref={rangerRef}>
                {defaultInstanceSteps.length > 0 && defaultValue !== null &&
                    <div className="slider__track-default-value"
                        style={{
                            left: `${defaultInstanceSteps[0].left}%`,
                            width: `${defaultInstanceSteps[0].width}%`
                        }}
                    />
                }
                {mainInstanceSteps.length > 0 && value !== null &&
                    <div className="slider__track-value"
                        style={{
                            left: `${mainInstanceSteps[0].left}%`,
                            width: `calc(${mainInstanceSteps[0].width}% + 5px)`
                        }}
                    />
                }
                {!disabled && mainInstance
                    .handles()
                    .map((handle, i) => (
                        <button
                            key={i}
                            type="button"
                            className={cn("slider__handle", {
                                "slider__handle--active": handle.isActive,
                            })}
                            onKeyDown={handle.onKeyDownHandler}
                            onMouseDown={handle.onMouseDownHandler}
                            onTouchStart={handle.onTouchStart}
                            role="slider"
                            aria-valuemin={mainInstance.options.min}
                            aria-valuemax={mainInstance.options.max}
                            aria-valuenow={handle.value}
                            style={{ left: `${mainInstance.getPercentageForValue(handle.value)}%` }}
                        />
                    ),
                    )}
            </div>
            {(!!validationMessage || !!valueLabel || !!defaultValueLabel) &&
                <Flex className="mt-1" alignItems="baseline"
                    justifyContent={validationMessage && (valueLabel || defaultValueLabel) ? "space-between" : (!validationMessage && (valueLabel || defaultValueLabel) ? "flex-end" : undefined)}>
                    {!!validationMessage &&
                        <FlexItem>
                            <ValidationMessage>{validationMessage}</ValidationMessage>
                        </FlexItem>
                    }
                    {!!(valueLabel || defaultValueLabel) &&
                        <FlexItem shrink={0} className="text-right">
                            {value !== null ? valueLabel : defaultValueLabel}
                        </FlexItem>
                    }
                </Flex>
            }
        </div>
    )
}

export default Slider
